import React, { useEffect, useState } from 'react';
import { 
    Card, 
    Button, 
    Space, 
    Switch, 
    Select,
    Modal,
    Typography,
    Breadcrumb
} from 'antd';
import { 
    PlusOutlined, 
    ReloadOutlined,
    HomeOutlined,
    SettingOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTransactionMenus } from '../hooks/useTransactionMenus';
import { TransactionMenuTable } from '../components/TransactionMenuTable';
import { TransactionMenuForm } from '../components/TransactionMenuForm';
import { TransactionMenuConfigDTO, TransactionMenuFormValues, TransactionType } from '../types';

const { Title } = Typography;

export const TransactionMenuListView: React.FC = () => {
    const {
        menus,
        transactionTypes,
        isLoading,
        isSubmitting,
        filterType,
        setFilterType,
        fetchAllMenus,
        fetchTransactionTypes,
        createMenu,
        updateMenu,
        deleteMenu,
        activateMenu,
        deactivateMenu
    } = useTransactionMenus();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMenu, setEditingMenu] = useState<TransactionMenuConfigDTO | null>(null);
    const [localShowInactive, setLocalShowInactive] = useState(false);

    // Initial load - only run once on mount
    useEffect(() => {
        fetchTransactionTypes();
        fetchAllMenus(localShowInactive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - only run on mount

    // Handle show inactive toggle
    const handleShowInactiveChange = (checked: boolean) => {
        setLocalShowInactive(checked);
        fetchAllMenus(checked);
    };

    const handleCreate = () => {
        setEditingMenu(null);
        setIsModalOpen(true);
    };

    const handleEdit = (menu: TransactionMenuConfigDTO) => {
        setEditingMenu(menu);
        setIsModalOpen(true);
    };

    const handleSubmit = async (values: TransactionMenuFormValues) => {
        let success: boolean;
        if (editingMenu?.id) {
            success = await updateMenu(editingMenu.id, values);
        } else {
            success = await createMenu(values);
        }
        if (success) {
            setIsModalOpen(false);
            setEditingMenu(null);
        }
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setEditingMenu(null);
    };

    const filteredMenus = filterType 
        ? menus.filter(m => m.transactionType === filterType)
        : menus;

    return (
        <div className="p-6">
            <Breadcrumb className="mb-4">
                <Breadcrumb.Item>
                    <Link to="/"><HomeOutlined /> Home</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Link to="/admin"><SettingOutlined /> Admin</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>Customize Transaction Menus</Breadcrumb.Item>
            </Breadcrumb>

            <Card>
                <div className="flex justify-between items-center mb-4">
                    <Title level={4} className="m-0">
                        Transaction Menu Configuration
                    </Title>
                    <Space>
                        <Select
                            placeholder="Filter by type"
                            allowClear
                            style={{ width: 200 }}
                            value={filterType}
                            onChange={(value) => setFilterType(value || null)}
                            options={transactionTypes.map(t => ({
                                value: t.value,
                                label: t.label
                            }))}
                        />
                        <Space>
                            <span>Show Inactive:</span>
                            <Switch
                                checked={localShowInactive}
                                onChange={handleShowInactiveChange}
                            />
                        </Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => fetchAllMenus(localShowInactive)}
                        >
                            Refresh
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleCreate}
                        >
                            Add Menu
                        </Button>
                    </Space>
                </div>

                <TransactionMenuTable
                    menus={filteredMenus}
                    isLoading={isLoading}
                    onEdit={handleEdit}
                    onDelete={deleteMenu}
                    onActivate={activateMenu}
                    onDeactivate={deactivateMenu}
                />
            </Card>

            <Modal
                title={editingMenu ? 'Edit Transaction Menu' : 'Create Transaction Menu'}
                open={isModalOpen}
                onCancel={handleCancel}
                footer={null}
                width={800}
                destroyOnClose
            >
                <TransactionMenuForm
                    initialValues={editingMenu}
                    transactionTypes={transactionTypes}
                    isSubmitting={isSubmitting}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                />
            </Modal>
        </div>
    );
};

export default TransactionMenuListView;
